import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { useResponsive } from '../hooks/useResponsive';

/**
 * Standalone splash page for deleted accounts
 * No complex context dependencies - just checks user.deleted_at directly
 */
const AccountDeletionSplash: React.FC = () => {
  const { isMobile } = useResponsive();
  const { user, sessionToken, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [isReactivating, setIsReactivating] = useState(false);

  // Calculate deletion info from user object
  const deletedAt = user?.deleted_at ? new Date(user.deleted_at) : null;
  const hardDeleteDate = deletedAt ? new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
  // Clamp at 0 — once the hard-delete date has passed, Math.ceil would go
  // negative and render "-2 Days" until permanent deletion.
  const daysRemaining = hardDeleteDate ? Math.max(0, Math.ceil((hardDeleteDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))) : 0;

  // If user is NOT deleted, redirect away
  useEffect(() => {
    if (user && !user.deleted_at) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleReactivate = async () => {
    if (!sessionToken) {
      toast.error('Authentication required');
      return;
    }

    setIsReactivating(true);

    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/account/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reactivate account');
      }

      toast.success('Your account has been reactivated!');
      
      // Refresh the page to reload user data
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);

    } catch (error) {
      console.error('Reactivation error:', error);
      toast.error('Failed to reactivate account. Please try again.');
      setIsReactivating(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Session still resolving — hold the loader briefly.
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafbff'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Deep-linked here while signed out (no session) — there's no account to
  // reactivate and nothing to render, so we'd otherwise hang on "Loading…"
  // forever. Send them to sign in instead.
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If somehow we're here without deletion, show error
  if (!user.deleted_at) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafbff'
      }}>
        <div>Redirecting...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fafbff',
      padding: isMobile ? '12px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: isMobile ? '0' : '16px',
        boxShadow: '0 4px 20px rgba(21, 17, 51, 0.1)',
        maxWidth: isMobile ? '100%' : '500px',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #151133 0%, #2a1a5e 100%)',
          padding: isMobile ? '24px' : '32px',
          textAlign: 'center',
          color: 'white'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '8px'
          }}>
            Account Scheduled for Deletion
          </h1>
          <div style={{
            fontSize: '48px',
            fontWeight: '800',
            color: '#f9550d',
            marginTop: '16px'
          }}>
            {daysRemaining} {daysRemaining === 1 ? 'Day' : 'Days'}
          </div>
          <div style={{
            fontSize: '14px',
            opacity: 0.9,
            marginTop: '8px'
          }}>
            Until permanent deletion
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: isMobile ? '24px' : '32px' }}>
          <div style={{
            backgroundColor: '#fff3e0',
            border: '1px solid #ffe0b2',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{ fontSize: '24px', color: '#F59E0B', fontWeight: '700' }}>!</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: '600',
                  color: '#151133',
                  marginBottom: '8px'
                }}>
                  Your account will be permanently deleted on:
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#f9550d'
                }}>
                  {hardDeleteDate?.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: '#fafbff',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#151133',
              marginBottom: '12px'
            }}>
              What will be deleted:
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              color: '#6c757d',
              fontSize: '14px',
              lineHeight: '1.8'
            }}>
              <li>Profile and account information</li>
              <li>All athlete profiles in the family</li>
              <li>Messages and conversations</li>
              <li>School lists and preferences</li>
              <li>All uploaded documents and media</li>
            </ul>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              onClick={handleReactivate}
              disabled={isReactivating}
              style={{
                background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isReactivating ? 'not-allowed' : 'pointer',
                opacity: isReactivating ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {isReactivating ? 'Reactivating...' : 'Reactivate My Account'}
            </button>

            <button
              onClick={handleLogout}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletionSplash;
